import { Component, OnInit, OnDestroy, HostListener, ElementRef, NgZone } from '@angular/core';

import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

import { ProofreaderService, ProofTarget } from './proofreader.service';

interface ProofEdit {
  before: string;
  after: string;
  type?: string;
  reason?: string;
}

type ProofStatus = 'idle' | 'checking' | 'clean' | 'suggestions' | 'error';

@Component({
  selector: 'proofreader-overlay',
  templateUrl: './proofreader-overlay.component.html',
  styleUrls: ['./proofreader-overlay.component.scss'],
  standalone: false
})
export class ProofreaderOverlayComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private repositionHandler = () => this.reposition();

  // The editable currently being tracked, and where to paint the badge for it.
  activeTarget: ProofTarget | null = null;
  badgePos: { top: number; left: number } | null = null;
  panelPos: { top?: number; bottom?: number; right: number } | null = null;

  panelOpen = false;
  status: ProofStatus = 'idle';
  edits: ProofEdit[] = [];
  appliedCount = 0;

  private readonly badgeSize = 26;

  constructor(
    private _proofreader: ProofreaderService,
    private _elementRef: ElementRef,
    private _zone: NgZone
  ) {}


  ngOnInit() {
    // Move our host to be a direct child of <body> so it lives in the root stacking context,
    // above CDK dialog overlays (the email composer etc. render in .cdk-overlay-container).
    // Otherwise an app-root ancestor's stacking context can trap the badge behind dialogs.
    try { document.body.appendChild(this._elementRef.nativeElement); } catch (_e) { /* noop */ }

    this._proofreader.installCkeditorHook();

    // CKEditor focus arrives out-of-band (it lives in an iframe).
    this._proofreader.ckeditorFocus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(target => {
      this._zone.run(() => this.activate(target));
    });

    // Reposition the badge/panel as inner containers or the window scroll. Capture phase
    // catches scrolls on nested scroll containers, not just window. Run outside Angular to
    // avoid change-detection thrash; reposition() flips back in via requestAnimationFrame.
    this._zone.runOutsideAngular(() => {
      document.addEventListener('scroll', this.repositionHandler, true);
      window.addEventListener('resize', this.repositionHandler);
    });
  }


  ngOnDestroy() {
    document.removeEventListener('scroll', this.repositionHandler, true);
    window.removeEventListener('resize', this.repositionHandler);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }


  @HostListener('document:focusin', ['$event'])
  onFocusIn(event: FocusEvent) {
    const el = event.target as HTMLElement;
    if (this._elementRef.nativeElement.contains(el)) return; // Focus inside our own UI.

    const target = this._proofreader.buildTargetFromElement(el);
    if (target) {
      this.activate(target);
    } else if (!this.panelOpen) {
      this.deactivate(); // Focus left a field for something non-editable; hide the badge.
    }
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const node = event.target as Node;
    if (this._elementRef.nativeElement.contains(node)) return;     // Our own badge/panel.
    if (this.activeTarget && this.activeTarget.el.contains(node)) return; // The field itself.
    // Clicked away from the field and our UI — close the panel and retire the badge.
    this.deactivate();
  }


  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.panelOpen) this.closePanel();
    else this.deactivate();
  }


  // Re-home our host so the badge/panel can never be painted over by the focused field.
  // This app's CDK dialogs (e.g. the email composer) render in the browser TOP LAYER
  // (popover="manual"), which paints above the entire normal layer regardless of z-index —
  // so to show over them we must mount INSIDE that same top-layer subtree. For non-dialog
  // fields we mount at <body>.
  private mountHostFor(target: ProofTarget) {
    const host = this._elementRef.nativeElement as HTMLElement;
    const topLayer = target.el.closest('[popover], .cdk-overlay-popover, .cdk-overlay-container') as HTMLElement;
    const container = topLayer || document.body;
    if (host.parentElement !== container) container.appendChild(host);
  }


  private activate(target: ProofTarget) {
    this.mountHostFor(target);
    const switching = this.activeTarget?.el !== target.el;
    this.activeTarget = target;
    if (switching) {
      this.panelOpen = false;
      this.status = 'idle';
      this.edits = [];
    }
    this.reposition();
  }


  private deactivate() {
    this.activeTarget = null;
    this.badgePos = null;
    this.panelOpen = false;
  }


  private reposition() {
    if (!this.activeTarget) return;

    // Field was removed from the DOM (e.g. edit form cancelled) — drop the badge entirely.
    if (!this.activeTarget.el.isConnected) {
      this._zone.run(() => this.deactivate());
      return;
    }

    const rect = this.activeTarget.el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // Field scrolled fully out of view — hide until it comes back.
    if (rect.bottom < 0 || rect.top > vh) {
      this._zone.run(() => { this.badgePos = null; });
      return;
    }

    // Anchor the badge OUTSIDE the field so it never covers the text the user is
    // writing. Candidate ladder: (1) outside the right edge — vertically centered for
    // single-line inputs, bottom-aligned for tall fields; (2) above the field's right
    // corner; (3) below it. A candidate is skipped when it's off-screen or when
    // something interactive already lives there (composer send buttons, clear icons —
    // seen in message-items-list). Last resort only: the old inside-corner spot.
    const gap = 6;
    const isShortField = rect.height <= 60;
    const outsideTop = isShortField
      ? rect.top + ((rect.height - this.badgeSize) / 2)
      : rect.bottom - this.badgeSize - gap;

    const candidates = [
      { top: outsideTop, left: rect.right + gap },                                 // outside right
      { top: rect.top - this.badgeSize - gap, left: rect.right - this.badgeSize - gap }, // above right corner
      { top: rect.bottom + gap, left: rect.right - this.badgeSize - gap }          // below right corner
    ];

    let badgeTop = Math.min(Math.max(outsideTop, 8), vh - this.badgeSize - 8);
    let left = rect.right - this.badgeSize - gap; // inside corner — last resort

    for (const _candidate of candidates) {
      const onScreen = _candidate.top >= 8 && (_candidate.top + this.badgeSize) <= (vh - 8)
        && _candidate.left >= 8 && (_candidate.left + this.badgeSize) <= (vw - 4);
      if (!onScreen) continue;
      if (!this.badgeSpotIsFree(_candidate.left, _candidate.top)) continue;

      badgeTop = _candidate.top;
      left = _candidate.left;
      break;
    }

    const right = Math.max(8, vw - rect.right);

    // Open the panel below the field when there's room; otherwise above it (bottom-anchored),
    // so it never spills off the bottom of the viewport.
    const openAbove = rect.bottom > vh * 0.6;
    const panelPos = openAbove
      ? { bottom: Math.max(8, vh - rect.top + 8), right }
      : { top: rect.bottom + 8, right };

    this._zone.run(() => {
      this.badgePos = { top: badgeTop, left };
      this.panelPos = panelPos;
    });
  }


  // A badge spot is "free" when nothing interactive is already painted there —
  // sitting on a send button or icon would both hide it and steal its clicks.
  private badgeSpotIsFree(left: number, top: number): boolean {
    const hit = document.elementFromPoint(left + (this.badgeSize / 2), top + (this.badgeSize / 2));
    if (!hit) return true;

    const host = this._elementRef.nativeElement as HTMLElement;
    if (host.contains(hit)) return true; // it's our own badge from the previous paint

    return !(hit as HTMLElement).closest('button, a, input, textarea, select, [role="button"], mat-icon');
  }


  // --- Badge / proofread flow ---

  onBadgeClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.activeTarget) return;

    if (this.panelOpen) { this.closePanel(); return; }

    const text = (this.activeTarget.getText() || '').trim();
    if (!text.length) {
      this.status = 'clean';
      this.edits = [];
      this.panelOpen = true;
      return;
    }

    this.status = 'checking';
    this.edits = [];
    this.appliedCount = 0;
    this.panelOpen = true;

    const mode = 'grammar';
    this._proofreader.proofread(this.activeTarget.getText(), mode)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res) => {
          const edits: ProofEdit[] = Array.isArray(res?.edits) ? res.edits : [];
          this.lastCorrected = (typeof res?.corrected === 'string') ? res.corrected : null;
          this.edits = edits;
          this.status = edits.length ? 'suggestions' : 'clean';
        },
        error: () => { this.status = 'error'; }
      });
  }


  private lastCorrected: string | null = null;


  applyAll() {
    if (!this.activeTarget || this.lastCorrected == null) return;
    this.activeTarget.setText(this.lastCorrected);
    this.appliedCount = this.edits.length;
    this.edits = [];
    this.status = 'clean';
  }


  // Applies a single suggestion by replacing its first occurrence in the live text.
  applyEdit(edit: ProofEdit, index: number) {
    if (!this.activeTarget) return;
    const current = this.activeTarget.getText();
    if (edit.before && current.includes(edit.before)) {
      this.activeTarget.setText(current.replace(edit.before, edit.after ?? ''));
    }
    this.edits.splice(index, 1);
    this.appliedCount++;
    if (!this.edits.length) this.status = 'clean';
  }


  dismissEdit(index: number) {
    this.edits.splice(index, 1);
    if (!this.edits.length) this.status = 'clean';
  }


  closePanel() {
    this.panelOpen = false;
  }
}
